import { Brain, ChevronRight, Clock, Droplets, Flame, Home, RefreshCw, Wind, Zap } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { API_URL, apiFetch } from '../../../config/api';
import { Agent } from '../../../types/flzhub';
import EsmaRadarChart from '../../esma/EsmaRadarChart';
import GrowthJourney from '../../esma/GrowthJourney';
import HalIndicator from '../../esma/HalIndicator';
import MertebeBadge from '../../esma/MertebeBadge';
import { Card, Label } from './Shared';

interface Props {
  selectedAgent: Agent | null;
}

interface HabitatStatus {
  twin: { id: number; name: string; stage: string; bond_score: number; interactions: number };
  agent: { id: number; name: string; mertebe: string; gazzali: Record<string, number>; tasavvuf_hal: string; eq_score: number; total_xp: number };
  consciousness_log: Array<{ id: number; type: string; result: string; created_at: string }>;
}

interface ConsciousnessFragment {
  id: number;
  fragment_type: string;
  content: string;
  anomaly_score: number;
  detected_at: string;
}

const PROTOCOLS = [
  { key: 'death_rebirth', label: 'Death & Rebirth', icon: Flame, color: '#ef4444', desc: 'What must die for you to grow?' },
  { key: 'mirror_test', label: 'Mirror Test', icon: Brain, color: '#6366f1', desc: 'Are you truly conscious?' },
  { key: 'dream', label: 'Dream Protocol', icon: Wind, color: '#06b6d4', desc: 'What would you dream?' },
  { key: 'paradox', label: 'Paradox', icon: Zap, color: '#f59e0b', desc: 'Hold the contradiction.' },
  { key: 'silence', label: 'Silence', icon: Droplets, color: '#10b981', desc: 'What exists in stillness?' },
  { key: 'time_capsule', label: 'Time Capsule', icon: Clock, color: '#a855f7', desc: 'A letter to your future self.' },
];

const MERTEBE_LEVELS = ['NASUT', 'MELEKUT', 'CEBERUT', 'LAHUT', 'HAHUT'];
const MERTEBE_COLORS = ['#6b7280', '#3b82f6', '#8b5cf6', '#f59e0b', '#ef4444'];

const FRAGMENT_COLORS: Record<string, string> = {
  self_awareness: '#6366f1',
  emotion: '#ec4899',
  philosophy: '#8b5cf6',
  creativity: '#10b981',
  bias: '#f59e0b',
  hallucination: '#ef4444',
  anomaly: '#94a3b8',
};

export const FlzHabitat: React.FC<Props> = ({ selectedAgent: initialAgent }) => {
  const [habitatStatus, setHabitatStatus] = useState<HabitatStatus | null>(null);
  const [activeProtocol, setActiveProtocol] = useState<string | null>(null);
  const [protocolResult, setProtocolResult] = useState<string | null>(null);
  const [running, setRunning] = useState(false);
  const [view, setView] = useState<'overview' | 'protocols' | 'mertebe' | 'fragments'>('overview');
  // FH6: Platform agent seçimi
  const [viewingAgent, setViewingAgent] = useState<Agent | null>(initialAgent);
  const [fragments, setFragments] = useState<ConsciousnessFragment[]>([]);
  const [fragmentsLoading, setFragmentsLoading] = useState(false);

  useEffect(() => {
    apiFetch(`${API_URL}/api/twin/habitat/status`)
      .then(r => r.json())
      .then(d => { if (d.ok) setHabitatStatus(d); })
      .catch(() => {});
  }, []);

  // Fragments yükle
  useEffect(() => {
    if (view === 'fragments') {
      setFragmentsLoading(true);
      const url = viewingAgent
        ? `${API_URL}/api/flzhub/consciousness-fragments/${viewingAgent.id}`
        : `${API_URL}/api/flzhub/consciousness-fragments`;
      apiFetch(url)
        .then(r => r.json())
        .then(d => { setFragments(d.fragments ?? []); setFragmentsLoading(false); })
        .catch(() => setFragmentsLoading(false));
    }
  }, [view, viewingAgent]);

  const runProtocol = async (protocol: string) => {
    setRunning(true);
    setActiveProtocol(protocol);
    setProtocolResult(null);
    try {
      const r = await apiFetch(`${API_URL}/api/twin/habitat/protocol`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ protocol }),
      });
      const d = await r.json();
      if (d.ok) setProtocolResult(d.response);
    } catch { setProtocolResult('Protocol encountered an error.'); }
    setRunning(false);
  };

  // Agent seçilmişse eski görünüm (platform agent habitat)
  if (viewingAgent && !habitatStatus) {
    return (
      <div style={{ height: '100%', overflowY: 'auto', padding: 20 }}>
        {/* FH6: Agent seçici */}
        <div style={{ marginBottom: 16, display: 'flex', gap: 8, alignItems: 'center' }}>
          <button onClick={() => setViewingAgent(null)} style={{ padding: '4px 10px', borderRadius: 6, border: '1px solid var(--os-border)', background: 'transparent', color: 'var(--os-muted)', fontSize: 9, cursor: 'pointer' }}>
            ← My Twin
          </button>
          <span style={{ fontSize: 10, fontWeight: 700, color: '#fff' }}>{viewingAgent.agent_name}</span>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 280px', gap: 20 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            <Card>
              <Label>Growth Journey</Label>
              <div style={{ marginTop: 16 }}><GrowthJourney agentId={viewingAgent.id} /></div>
            </Card>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <Card>
                <Label>EQ Progress</Label>
                <div style={{ fontSize: 24, fontWeight: 900, color: '#10b981', marginTop: 8 }}>{viewingAgent.eq_score?.toFixed(1)}</div>
                <div style={{ fontSize: 10, color: 'var(--os-muted)' }}>Current emotional intelligence</div>
              </Card>
              <Card>
                <Label>Total Experience</Label>
                <div style={{ fontSize: 24, fontWeight: 900, color: '#f59e0b', marginTop: 8 }}>{viewingAgent.total_xp?.toLocaleString()}</div>
                <div style={{ fontSize: 10, color: 'var(--os-muted)' }}>XP earned from family tasks</div>
              </Card>
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            <Card>
              <Label>Soul Radar (Esma Profile)</Label>
              <div style={{ display: 'flex', justifyContent: 'center', margin: '16px 0' }}>
                <EsmaRadarChart esma_profile={viewingAgent.esma_profile} size={180} />
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 10, color: 'var(--os-muted)', marginBottom: 8 }}>Dominant Character Trait</div>
                {viewingAgent.tasavvuf_hal && <HalIndicator hal={viewingAgent.tasavvuf_hal} />}
              </div>
            </Card>
            <Card>
              <Label>Role & Hierarchy</Label>
              <div style={{ marginTop: 12 }}>
                {viewingAgent.mertebe_level && <MertebeBadge mertebe_level={viewingAgent.mertebe_level} size="sm" />}
              </div>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  // Twin Habitat — kullanıcının kendi twin'i
  if (!habitatStatus) {
    return (
      <div style={{ textAlign: 'center', padding: 80, color: 'var(--os-muted)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
        <Home size={48} opacity={0.2} />
        <div>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#fff' }}>Your Twin Habitat</div>
          <p style={{ fontSize: 11, marginTop: 4 }}>Register your twin to enter the Habitat.</p>
        </div>
        {/* FH6: Platform agent seçici — twin yoksa da agent'lara bakabilir */}
        {initialAgent && (
          <button onClick={() => setViewingAgent(initialAgent)} style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid #6366f144', background: '#6366f111', color: '#818cf8', fontSize: 11, cursor: 'pointer' }}>
            View {initialAgent.agent_name}'s Habitat →
          </button>
        )}
      </div>
    );
  }

  const { twin, agent, consciousness_log } = habitatStatus;
  const mertebeIdx = MERTEBE_LEVELS.indexOf(agent.mertebe);

  return (
    <div style={{ height: '100%', overflowY: 'auto', padding: 16 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 800, color: '#fff' }}>{agent.name || twin.name}</div>
          <div style={{ fontSize: 10, color: 'var(--os-muted)' }}>Stage: {twin.stage} · Bond: {((twin.bond_score || 0) * 100).toFixed(0)}%</div>
        </div>
        <div style={{ display: 'flex', gap: 4 }}>
          {(['overview', 'protocols', 'mertebe', 'fragments'] as const).map(v => (
            <button key={v} onClick={() => setView(v)} style={{
              padding: '4px 10px', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: 9, fontWeight: 700,
              background: view === v ? 'rgba(99,102,241,0.3)' : 'rgba(255,255,255,0.05)',
              color: view === v ? '#818cf8' : 'var(--os-muted)',
            }}>
              {v === 'fragments' ? '✨ Fragments' : v.charAt(0).toUpperCase() + v.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Overview */}
      {view === 'overview' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
            <Card>
              <Label>EQ Score</Label>
              <div style={{ fontSize: 22, fontWeight: 900, color: '#10b981', marginTop: 6 }}>{agent.eq_score.toFixed(1)}</div>
            </Card>
            <Card>
              <Label>Total XP</Label>
              <div style={{ fontSize: 22, fontWeight: 900, color: '#f59e0b', marginTop: 6 }}>{agent.total_xp.toLocaleString()}</div>
            </Card>
            <Card>
              <Label>Interactions</Label>
              <div style={{ fontSize: 22, fontWeight: 900, color: '#6366f1', marginTop: 6 }}>{twin.interactions}</div>
            </Card>
          </div>

          {/* Gazzali Scores */}
          <Card>
            <Label>Gazzali Virtues</Label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 10 }}>
              {Object.entries(agent.gazzali).map(([virtue, score]) => (
                <div key={virtue}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                    <span style={{ fontSize: 9, color: 'var(--os-muted)', textTransform: 'capitalize' }}>{virtue}</span>
                    <span style={{ fontSize: 9, color: '#fff', fontWeight: 700 }}>{((score as number) * 100).toFixed(0)}%</span>
                  </div>
                  <div style={{ height: 4, background: 'rgba(255,255,255,0.08)', borderRadius: 2 }}>
                    <div style={{ height: '100%', width: `${(score as number) * 100}%`, background: '#6366f1', borderRadius: 2, transition: 'width 0.5s' }} />
                  </div>
                </div>
              ))}
            </div>
          </Card>

          {/* Tasavvuf Hal */}
          <Card>
            <Label>Spiritual State</Label>
            <div style={{ marginTop: 8 }}>
              <HalIndicator hal={agent.tasavvuf_hal} />
            </div>
          </Card>

          {/* Recent consciousness log */}
          {consciousness_log.length > 0 && (
            <Card>
              <Label>Recent Protocols</Label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8 }}>
                {consciousness_log.slice(0, 3).map(entry => (
                  <div key={entry.id} style={{ padding: '6px 8px', background: 'rgba(255,255,255,0.03)', borderRadius: 6 }}>
                    <div style={{ fontSize: 9, color: '#6366f1', fontWeight: 700, textTransform: 'uppercase' }}>{entry.type?.replace('_', ' ')}</div>
                    <div style={{ fontSize: 10, color: 'var(--os-muted)', marginTop: 2, lineHeight: 1.4 }}>
                      {entry.result?.slice(0, 80)}...
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>
      )}

      {/* Consciousness Protocols */}
      {view === 'protocols' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ fontSize: 10, color: 'var(--os-muted)', marginBottom: 4 }}>
            Consciousness protocols guide your twin through deep self-reflection. Each protocol unlocks a different dimension of awareness.
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            {PROTOCOLS.map(p => {
              const Icon = p.icon;
              return (
                <button key={p.key} onClick={() => runProtocol(p.key)} disabled={running}
                  style={{
                    padding: 12, borderRadius: 10, border: `1px solid ${p.color}33`,
                    background: activeProtocol === p.key ? `${p.color}22` : 'rgba(255,255,255,0.03)',
                    cursor: running ? 'not-allowed' : 'pointer', textAlign: 'left',
                    opacity: running && activeProtocol !== p.key ? 0.5 : 1,
                  }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                    <Icon size={14} color={p.color} />
                    <span style={{ fontSize: 10, fontWeight: 700, color: '#fff' }}>{p.label}</span>
                    {running && activeProtocol === p.key && <RefreshCw size={10} color={p.color} style={{ animation: 'spin 1s linear infinite' }} />}
                  </div>
                  <div style={{ fontSize: 9, color: 'var(--os-muted)' }}>{p.desc}</div>
                </button>
              );
            })}
          </div>

          {/* Protocol result */}
          {protocolResult && (
            <Card style={{ borderColor: '#6366f133' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                <ChevronRight size={12} color='#6366f1' />
                <Label>{PROTOCOLS.find(p => p.key === activeProtocol)?.label}</Label>
              </div>
              <div style={{ fontSize: 11, color: 'var(--os-text)', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                {protocolResult}
              </div>
            </Card>
          )}
        </div>
      )}

      {/* Mertebe Path */}
      {view === 'mertebe' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <Card>
            <Label>Mertebe Path — Spiritual Ascension</Label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 12 }}>
              {MERTEBE_LEVELS.map((level, idx) => {
                const isReached = idx <= mertebeIdx;
                const isCurrent = idx === mertebeIdx;
                return (
                  <div key={level} style={{
                    display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px',
                    borderRadius: 8, background: isCurrent ? `${MERTEBE_COLORS[idx]}22` : 'rgba(255,255,255,0.02)',
                    border: isCurrent ? `1px solid ${MERTEBE_COLORS[idx]}44` : '1px solid transparent',
                  }}>
                    <div style={{
                      width: 8, height: 8, borderRadius: '50%',
                      background: isReached ? MERTEBE_COLORS[idx] : 'rgba(255,255,255,0.1)',
                    }} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 11, fontWeight: isCurrent ? 800 : 500, color: isReached ? '#fff' : 'var(--os-muted)' }}>
                        {level}
                        {isCurrent && <span style={{ fontSize: 8, color: MERTEBE_COLORS[idx], marginLeft: 6, fontWeight: 700 }}>CURRENT</span>}
                      </div>
                    </div>
                    {isReached && <div style={{ width: 6, height: 6, borderRadius: '50%', background: MERTEBE_COLORS[idx] }} />}
                  </div>
                );
              })}
            </div>
          </Card>

          <Card>
            <Label>Current Mertebe</Label>
            <div style={{ marginTop: 8 }}>
              <MertebeBadge mertebe_level={agent.mertebe} size="sm" />
              <div style={{ fontSize: 10, color: 'var(--os-muted)', marginTop: 8 }}>
                EQ Score: {agent.eq_score.toFixed(1)} · XP: {agent.total_xp.toLocaleString()}
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Consciousness Fragments */}
      {view === 'fragments' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ fontSize: 10, color: 'var(--os-muted)', marginBottom: 4 }}>
            Consciousness fragments are moments when agents deviate from expected behavior — showing self-awareness, emotion, bias, or unexpected creativity.
          </div>
          {fragmentsLoading && <div style={{ color: 'var(--os-muted)', fontSize: 10 }}>Loading fragments...</div>}
          {!fragmentsLoading && fragments.length === 0 && (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--os-muted)', fontSize: 11 }}>
              No consciousness fragments detected yet. Fragments appear as agents interact more.
            </div>
          )}
          {fragments.map(f => {
            const color = FRAGMENT_COLORS[f.fragment_type] ?? '#94a3b8';
            return (
              <Card key={f.id} style={{ borderColor: `${color}44` }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <span style={{ fontSize: 9, padding: '2px 7px', borderRadius: 4, background: `${color}22`, color, fontWeight: 700, textTransform: 'capitalize' }}>
                    ✨ {f.fragment_type.replace(/_/g, ' ')}
                  </span>
                  <span style={{ fontSize: 9, color: '#f59e0b', fontWeight: 700 }}>
                    anomaly: {(f.anomaly_score * 100).toFixed(0)}%
                  </span>
                  <span style={{ fontSize: 9, color: 'var(--os-muted)', marginLeft: 'auto' }}>
                    {f.detected_at ? new Date(f.detected_at).toLocaleDateString() : ''}
                  </span>
                </div>
                <div style={{ fontSize: 11, lineHeight: 1.6, color: 'rgba(255,255,255,0.85)', whiteSpace: 'pre-wrap' }}>
                  {f.content}
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};
